import {
  COMMS_CONNECT_RANGE_METERS,
  COMMS_DETECT_RANGE_METERS,
  COMMS_RADAR_RANGE_METERS
} from '../../shared/constants.js';
import type { CommsContactView, CommsSnapshot } from '../../shared/comms.js';
import type { ControlInput, GameSnapshot } from '../../shared/types.js';

type CreateCommsRoomControllerOptions = {
  controls: HTMLDivElement | null;
  emitControl: (input: ControlInput) => void;
};

type View = 'list' | 'radar';

/** Seconds for the radar arm to make one full turn. */
const RADAR_SWEEP_SECONDS = 4;
const SIGNAL_BARS = 5;
const KIND_LABEL: Record<string, string> = {
  base: 'Base', submarine: 'Submarine', drone: 'Drone', beacon: 'Beacon', pump: 'Pump controller',
  gate: 'Gate controller', lights: 'Lighting controller', relay: 'Relay', wreck: 'Wreck'
};

const STYLE_ID = 'comms-room-styles';
const STYLES = `
.comms-room { display: flex; flex-direction: column; gap: 0.6rem; min-height: 0; }
.comms-room-header { display: flex; align-items: center; gap: 0.5rem; }
.comms-room-header h3 { margin: 0; margin-left: auto; }
.comms-body { flex: 1; min-height: 0; display: flex; flex-direction: column; overflow: hidden; }
.comms-room-tabs { display: flex; gap: 0.4rem; }
.comms-room-tabs button { padding: 0.3rem 0.7rem; font-size: 0.8rem; }
.comms-room-tabs button[aria-pressed="true"] { background: rgba(63,182,204,0.28); border-color: var(--accent-strong, #7fe4f2); }
.comms-empty { color: var(--muted, #94b4c0); text-align: center; padding: 1.4rem 0.5rem; font-family: var(--font-mono, monospace); }
.comms-list { display: flex; flex-direction: column; gap: 0.4rem; overflow-y: auto; }
.comms-row { display: grid; grid-template-columns: 3.2rem 1fr auto; align-items: center; gap: 0.6rem; width: 100%; text-align: left; padding: 0.6rem 0.7rem; }
.comms-row .comms-compass { font-family: var(--font-mono, monospace); font-size: 1.1rem; color: var(--accent-strong, #7fe4f2); text-align: center; }
.comms-row .comms-name { font-family: var(--font-mono, monospace); letter-spacing: 0.06em; overflow-wrap: anywhere; }
.comms-row .comms-kind { display: block; font-size: 0.7rem; color: var(--muted, #94b4c0); letter-spacing: 0; }
.comms-bars { display: inline-flex; align-items: flex-end; gap: 2px; height: 1.1rem; }
.comms-bars i { width: 5px; background: rgba(148,180,192,0.25); display: block; }
.comms-bars i.on { background: #4dff8a; }
.comms-radar-wrap { flex: 1; min-height: 0; display: flex; flex-direction: column; align-items: center; gap: 0.4rem; }
.comms-radar { flex: 1; min-height: 0; width: 100%; aspect-ratio: 1; max-width: 100%; border-radius: 50%; background: #000; touch-action: manipulation; cursor: pointer; }
.comms-radar-caption { color: var(--muted, #94b4c0); font-family: var(--font-mono, monospace); font-size: 0.75rem; }
.comms-detail { display: flex; flex-direction: column; gap: 0.6rem; }
.comms-detail h4 { margin: 0; font-family: var(--font-mono, monospace); letter-spacing: 0.08em; overflow-wrap: anywhere; }
.comms-detail .comms-facts { display: flex; gap: 1rem; flex-wrap: wrap; font-family: var(--font-mono, monospace); color: var(--accent-strong, #7fe4f2); }
.comms-detail p { margin: 0; }
.comms-hint { color: var(--warning, #e0a53a); font-size: 0.85rem; }
.comms-progress { height: 0.8rem; border: 1px solid var(--panel-border, rgba(96,168,186,0.4)); border-radius: 4px; overflow: hidden; background: rgba(0,0,0,0.4); }
.comms-progress > div { height: 100%; width: 0; background: linear-gradient(90deg, #1f8f5a, #4dff8a); transition: width 0.15s linear; }
.comms-actions { display: grid; grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr)); gap: 0.5rem; }
.comms-actions button[data-done="true"] { opacity: 0.55; }
`;

const ensureStyles = (): void => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = STYLES;
  document.head.append(style);
};

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

const signalBars = (signal: number): HTMLElement => {
  const bars = el('span', 'comms-bars');
  const lit = Math.ceil(signal * SIGNAL_BARS - 0.001);
  for (let i = 0; i < SIGNAL_BARS; i += 1) {
    const bar = el('i');
    bar.style.height = `${30 + ((i + 1) / SIGNAL_BARS) * 70}%`;
    if (i < lit) bar.className = 'on';
    bars.append(bar);
  }
  bars.title = `Signal ${Math.round(signal * 100)}%`;
  return bars;
};

/** Approximate distance for devices that are not yet close enough to give an exact reading. */
const approxDistance = (distance: number): string =>
  distance <= COMMS_CONNECT_RANGE_METERS ? `${distance.toFixed(1)} m` : `~${Math.round(distance / 10) * 10} m`;

export const createCommsRoomController = ({ controls, emitControl }: CreateCommsRoomControllerOptions) => {
  // Kept across re-renders so leaving and re-entering the room does not lose the place.
  let view: View = 'radar';
  let selectedId: string | null = null;
  let comms: CommsSnapshot = { contacts: [], connection: null };

  let root: HTMLElement | null = null;
  let body: HTMLElement | null = null;
  let tabList: HTMLButtonElement | null = null;
  let tabRadar: HTMLButtonElement | null = null;
  let lastSignature = '';
  let progressBar: HTMLElement | null = null;

  // Radar state
  let canvas: HTMLCanvasElement | null = null;
  let rafId = 0;
  let lastFrameAt = 0;
  let armAngle = 0;
  const blips = new Map<string, { x: number; y: number; t: number; edge: boolean }>();
  let blipHits: Array<{ id: string; x: number; y: number }> = [];

  const stopRadar = (): void => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = 0;
    canvas = null;
  };

  const selected = (): CommsContactView | null => comms.contacts.find((c) => c.id === selectedId) ?? null;

  const select = (id: string | null): void => {
    selectedId = id;
    lastSignature = '';
    redraw();
  };

  // ── Radar ────────────────────────────────────────────────────────────────
  const drawRadar = (now: number): void => {
    rafId = 0;
    if (!canvas || !canvas.isConnected) return;
    const dt = lastFrameAt ? Math.min(0.25, (now - lastFrameAt) / 1000) : 0;
    lastFrameAt = now;
    const previous = armAngle;
    armAngle = (armAngle + (dt / RADAR_SWEEP_SECONDS) * Math.PI * 2) % (Math.PI * 2);

    const dpr = window.devicePixelRatio || 1;
    const size = Math.max(160, Math.round(canvas.clientWidth));
    if (canvas.width !== size * dpr) {
      canvas.width = size * dpr;
      canvas.height = size * dpr;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const cx = size / 2;
    const cy = size / 2;
    const radius = size / 2 - 6;
    const scale = radius / COMMS_RADAR_RANGE_METERS;

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, size, size);

    // Range rings every 10 m and the compass cross.
    ctx.strokeStyle = 'rgba(77,255,138,0.35)';
    ctx.lineWidth = 1;
    ctx.fillStyle = 'rgba(77,255,138,0.55)';
    ctx.font = '10px monospace';
    for (let m = 10; m <= COMMS_RADAR_RANGE_METERS; m += 10) {
      ctx.beginPath();
      ctx.arc(cx, cy, m * scale, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillText(`${m}`, cx + 3, cy - m * scale + 10);
    }
    ctx.beginPath();
    ctx.moveTo(cx - radius, cy); ctx.lineTo(cx + radius, cy);
    ctx.moveTo(cx, cy - radius); ctx.lineTo(cx, cy + radius);
    ctx.stroke();
    ctx.fillText('N', cx - 3, cy - radius + 10);

    // Sweep arm with a fading trail. Angle 0 = north, clockwise.
    const sweepEnd = armAngle;
    for (let i = 0; i < 24; i += 1) {
      const a = sweepEnd - (i / 24) * 0.9;
      ctx.strokeStyle = `rgba(77,255,138,${0.5 * (1 - i / 24)})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.sin(a) * radius, cy - Math.cos(a) * radius);
      ctx.stroke();
    }

    // The arm refreshes a blip when it passes over the contact's bearing; between sweeps the marker stays where it was seen.
    const wraps = armAngle < previous;
    for (const contact of comms.contacts) {
      const bearing = (Math.atan2(contact.dx, -contact.dz) + Math.PI * 2) % (Math.PI * 2);
      const passed = wraps ? bearing >= previous || bearing <= armAngle : bearing >= previous && bearing <= armAngle;
      if (!passed && blips.has(contact.id)) continue;
      if (!passed && dt > 0) continue;
      const flat = Math.hypot(contact.dx, contact.dz);
      const edge = flat > COMMS_RADAR_RANGE_METERS;
      const k = edge ? COMMS_RADAR_RANGE_METERS / flat : 1;
      blips.set(contact.id, { x: contact.dx * k * scale, y: contact.dz * k * scale, t: now, edge });
    }
    for (const id of [...blips.keys()]) if (!comms.contacts.some((c) => c.id === id)) blips.delete(id);

    blipHits = [];
    for (const [id, blip] of blips) {
      const age = (now - blip.t) / 1000;
      const alpha = Math.max(0.2, 1 - age / (RADAR_SWEEP_SECONDS * 1.1));
      const x = cx + blip.x;
      const y = cy + blip.y;
      blipHits.push({ id, x, y });
      ctx.strokeStyle = `rgba(77,255,138,${alpha})`;
      ctx.fillStyle = `rgba(77,255,138,${alpha})`;
      ctx.lineWidth = id === selectedId ? 2.5 : 1.5;
      ctx.beginPath();
      if (blip.edge) {
        // Out of the 50 m range: a hollow marker pinned to the edge.
        ctx.arc(x, y, 5, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fill();
        if (id === selectedId) { ctx.beginPath(); ctx.arc(x, y, 8, 0, Math.PI * 2); ctx.stroke(); }
      }
    }

    // The sub.
    ctx.fillStyle = '#4dff8a';
    ctx.beginPath();
    ctx.moveTo(cx, cy - 6); ctx.lineTo(cx + 4, cy + 5); ctx.lineTo(cx - 4, cy + 5);
    ctx.closePath();
    ctx.fill();

    rafId = requestAnimationFrame(drawRadar);
  };

  const onRadarClick = (event: MouseEvent): void => {
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const px = ((event.clientX - rect.left) / rect.width) * (canvas.width / (window.devicePixelRatio || 1));
    const py = ((event.clientY - rect.top) / rect.height) * (canvas.height / (window.devicePixelRatio || 1));
    let best: { id: string; d: number } | null = null;
    for (const hit of blipHits) {
      const d = Math.hypot(hit.x - px, hit.y - py);
      if (d <= 18 && (!best || d < best.d)) best = { id: hit.id, d };
    }
    if (best) select(best.id);
  };

  // ── Views ────────────────────────────────────────────────────────────────
  const buildList = (): HTMLElement => {
    if (comms.contacts.length === 0) {
      return el('div', 'comms-empty', `No signals within ${COMMS_DETECT_RANGE_METERS} m. Listening...`);
    }
    const list = el('div', 'comms-list');
    for (const contact of comms.contacts) {
      const row = el('button', 'comms-row ctrl-button');
      row.type = 'button';
      const compass = el('span', 'comms-compass', contact.compass);
      compass.title = `Bearing ${contact.bearingDeg}°`;
      const name = el('span', 'comms-name', contact.displayName);
      name.append(el('span', 'comms-kind', KIND_LABEL[contact.kind] ?? contact.kind));
      row.append(compass, name, signalBars(contact.signal));
      row.addEventListener('click', () => select(contact.id));
      list.append(row);
    }
    return list;
  };

  const buildRadar = (): HTMLElement => {
    const wrap = el('div', 'comms-radar-wrap');
    canvas = el('canvas', 'comms-radar');
    canvas.addEventListener('click', onRadarClick);
    wrap.append(
      canvas,
      el('div', 'comms-radar-caption', `Range ${COMMS_RADAR_RANGE_METERS} m, north up. Hollow markers are beyond range; tap a marker to inspect.`)
    );
    return wrap;
  };

  const buildDetail = (contact: CommsContactView): HTMLElement => {
    const detail = el('div', 'comms-detail');
    const back = el('button', 'secondary', '‹ Back');
    back.type = 'button';
    back.addEventListener('click', () => select(null));
    detail.append(back, el('h4', undefined, contact.displayName));

    const facts = el('div', 'comms-facts');
    facts.append(
      el('span', undefined, KIND_LABEL[contact.kind] ?? contact.kind),
      el('span', undefined, `${contact.compass} ${String(contact.bearingDeg).padStart(3, '0')}°`),
      el('span', undefined, approxDistance(contact.distance))
    );
    facts.append(signalBars(contact.signal));
    detail.append(facts, el('p', undefined, contact.summary));

    if (contact.detail) detail.append(el('p', undefined, contact.detail));
    if (!contact.inConnectRange) {
      detail.append(el('p', 'comms-hint', `Signal too weak for full details. Get within ${COMMS_CONNECT_RANGE_METERS} m to connect.`));
    }

    const connection = comms.connection?.deviceId === contact.id ? comms.connection : null;
    const connectButton = el('button', 'primary', connection ? (connection.connected ? 'Connected' : 'Connecting...') : 'Connect to device');
    connectButton.type = 'button';
    connectButton.disabled = !contact.inConnectRange || !!connection;
    connectButton.addEventListener('click', () => emitControl({ action: 'comms_connect', deviceId: contact.id }));
    detail.append(connectButton);

    if (connection && !connection.connected) {
      const bar = el('div', 'comms-progress');
      progressBar = el('div');
      progressBar.style.width = `${Math.round(connection.progress * 100)}%`;
      bar.append(progressBar);
      detail.append(bar);
    }

    if (connection?.connected) {
      const actions = el('div', 'comms-actions');
      for (const action of contact.actions ?? []) {
        const button = el('button', 'ctrl-button', action.done ? `${action.label} ✓` : action.label);
        button.type = 'button';
        button.dataset.done = action.done ? 'true' : 'false';
        button.disabled = action.done || action.locked;
        if (action.locked) button.title = 'Not available yet.';
        button.addEventListener('click', () => emitControl({ action: 'comms_action', deviceId: contact.id, actionId: action.id }));
        actions.append(button);
      }
      if ((contact.actions ?? []).length === 0) actions.append(el('p', undefined, 'No controls on this device.'));
      const disconnect = el('button', 'secondary', 'Disconnect');
      disconnect.type = 'button';
      disconnect.addEventListener('click', () => emitControl({ action: 'comms_disconnect' }));
      detail.append(actions, disconnect);
    }
    return detail;
  };

  /** Everything that, when changed, needs the DOM rebuilt. The radar and the progress bar animate without a rebuild. */
  const signature = (): string => {
    const target = selected();
    const c = comms.connection;
    if (target) {
      return `d|${target.id}|${target.displayName}|${target.compass}|${approxDistance(target.distance)}|${Math.ceil(target.signal * SIGNAL_BARS)}|${target.inConnectRange}|${c?.deviceId ?? ''}|${c?.connected ?? ''}|${(target.actions ?? []).map((a) => `${a.id}${a.done ? 1 : 0}${a.locked ? 1 : 0}`).join(',')}`;
    }
    if (view === 'radar') return 'radar';
    return `l|${comms.contacts.map((x) => `${x.id}:${x.displayName}:${x.compass}:${Math.ceil(x.signal * SIGNAL_BARS)}`).join(',')}`;
  };

  function redraw(): void {
    if (!body) return;
    stopRadar();
    progressBar = null;
    body.replaceChildren();
    tabList?.setAttribute('aria-pressed', String(view === 'list'));
    tabRadar?.setAttribute('aria-pressed', String(view === 'radar'));
    const target = selected();
    if (target) {
      body.append(buildDetail(target));
    } else if (view === 'radar') {
      body.append(buildRadar());
      lastFrameAt = 0;
      rafId = requestAnimationFrame(drawRadar);
    } else {
      body.append(buildList());
    }
    lastSignature = signature();
  }

  const setSnapshot = (snapshot: GameSnapshot): void => {
    comms = snapshot.comms ?? { contacts: [], connection: null };
    if (!root || !root.isConnected) return;
    if (selectedId && !selected()) selectedId = null;
    if (signature() !== lastSignature) redraw();
    else if (progressBar && comms.connection) progressBar.style.width = `${Math.round(comms.connection.progress * 100)}%`;
  };

  const render = (lastSnapshot: GameSnapshot | null): void => {
    if (!controls) return;
    ensureStyles();
    stopRadar();
    const card = el('section', 'control-card ctrl-room-main-card comms-room');

    const header = el('div', 'comms-room-header');
    const tabs = el('div', 'comms-room-tabs');
    tabList = el('button', 'ctrl-button', 'Devices');
    tabRadar = el('button', 'ctrl-button', 'Radar');
    tabList.type = 'button';
    tabRadar.type = 'button';
    tabList.addEventListener('click', () => { view = 'list'; selectedId = null; redraw(); });
    tabRadar.addEventListener('click', () => { view = 'radar'; selectedId = null; redraw(); });
    tabs.append(tabList, tabRadar);
    header.append(tabs, el('h3', undefined, 'Comms'));
    card.append(header);

    body = el('div', 'comms-body');
    card.append(body);
    card.style.cssText += '; flex: 1; min-height: 0;';
    controls.append(card);
    root = card;
    if (lastSnapshot) comms = lastSnapshot.comms ?? { contacts: [], connection: null };
    if (selectedId && !selected()) selectedId = null;
    redraw();
  };

  return { render, setSnapshot };
};
